
import { db } from './firebase';
import { 
  doc, getDoc, setDoc, updateDoc, collection, 
  query, where, getDocs, arrayUnion, addDoc, limit, deleteDoc 
} from '@firebase/firestore';
import { Project, UserRole, UserSubscription } from '../types';
import { APP_CONFIG } from '../constants';

const cleanData = (data: any) => {
  return JSON.parse(JSON.stringify(data));
};

export interface UserProfile {
  uid: string;
  displayName: string;
  email: string;
  phone?: string;
  updatedAt: string;
}

export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
  try {
    const docRef = doc(db, "users", uid);
    const snap = await getDoc(docRef);
    if (snap.exists()) return snap.data() as UserProfile;
    return null;
  } catch (error) {
    console.error("[Database] Error fetching user profile:", error);
    return null;
  }
};

export const saveUserProfile = async (uid: string, profile: Partial<UserProfile>): Promise<void> => {
  try {
    const docRef = doc(db, "users", uid);
    const data = { ...profile, uid, updatedAt: new Date().toISOString() };
    await setDoc(docRef, cleanData(data), { merge: true });
  } catch (error) {
    console.error("[Database] Error saving user profile:", error);
  }
};

export const getUserProjects = async (userId: string): Promise<Project[]> => {
  if (!userId) return [];
  
  const projectsCol = collection(db, "projects");
  const projectsMap = new Map<string, Project>();

  try {
    const qOwner = query(projectsCol, where("ownerId", "==", userId));
    const snapOwner = await getDocs(qOwner);
    snapOwner.forEach(doc => {
      projectsMap.set(doc.id, { id: doc.id, ...doc.data() } as Project);
    });

    const qCollab = query(projectsCol, where("collaborators", "array-contains", userId));
    const snapCollab = await getDocs(qCollab);
    snapCollab.forEach(doc => {
      if (!projectsMap.has(doc.id)) {
        projectsMap.set(doc.id, { id: doc.id, ...doc.data() } as Project);
      }
    });

    const legacyDocRef = doc(db, "projects", userId);
    const legacySnap = await getDoc(legacyDocRef);
    if (legacySnap.exists() && !projectsMap.has(legacySnap.id)) {
       projectsMap.set(legacySnap.id, { id: legacySnap.id, ...legacySnap.data() } as Project);
    }

  } catch (error) {
    console.error("[Database] Error loading projects:", error);
  }

  return Array.from(projectsMap.values());
};

export const getProjectByPhotoToken = async (token: string): Promise<Project | null> => {
  try {
    const q = query(collection(db, "projects"), where("photoToken", "==", token), limit(1));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    
    const project = { id: snap.docs[0].id, ...snap.docs[0].data() } as Project;
    
    // Validação de segurança: Expiração do Token
    if (project.photoTokenExpiresAt) {
      const expirationDate = new Date(project.photoTokenExpiresAt);
      if (expirationDate < new Date()) {
        console.warn("[Security] Token de acesso expirado.");
        return null;
      }
    }

    return project;
  } catch (error) {
    console.error("[Database] Error fetching by token:", error);
    return null;
  }
};

export const saveUserProject = async (userId: string, project: Project): Promise<string> => {
  try {
    const { id, ...projectData } = project as any;
    
    const shareCode = projectData.shareCode || Math.random().toString(36).substring(2, 8).toUpperCase();
    
    // Geração segura de Token de Prestador com data de expiração
    const photoToken = crypto.randomUUID();
    const expiration = new Date();
    expiration.setDate(expiration.getDate() + APP_CONFIG.PHOTO_TOKEN_EXPIRATION_DAYS);
    
    const finalData = { 
      ...projectData, 
      ownerId: userId, 
      collaborators: projectData.collaborators || [],
      roles: projectData.roles || { [userId]: 'owner' },
      shareCode: shareCode,
      photoToken: photoToken,
      photoTokenExpiresAt: expiration.toISOString(),
      updatedAt: new Date().toISOString() 
    };
    
    const sanitizedData = cleanData(finalData);
    const docRef = await addDoc(collection(db, "projects"), sanitizedData);
    return docRef.id;
  } catch (error) {
    console.error("[Database] Error creating project:", error);
    throw new Error("Falha ao criar o projeto.");
  }
};

export const updateUserProject = async (projectId: string, updates: Partial<Project>): Promise<void> => {
  try {
    const docRef = doc(db, "projects", projectId);
    const sanitizedUpdates = cleanData({ ...updates, updatedAt: new Date().toISOString() });
    await updateDoc(docRef, sanitizedUpdates);
  } catch (error) {
    console.error("[Database] Error updating project:", error);
    throw error;
  }
};

export const deleteProject = async (projectId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, "projects", projectId));
  } catch (error) {
    console.error("[Database] Error deleting project:", error);
    throw error;
  }
};

export const joinProjectByToken = async (userId: string, code: string): Promise<string | null> => {
  try {
    const normalizedCode = code.trim().toUpperCase();
    const q = query(collection(db, "projects"), where("shareCode", "==", normalizedCode), limit(1));
    const snap = await getDocs(q);
    
    if (snap.empty) return null;

    const projectDoc = snap.docs[0];
    const projectData = projectDoc.data();
    
    if (projectData.ownerId === userId || (projectData.collaborators && projectData.collaborators.includes(userId))) {
      return projectDoc.id;
    }

    const docRef = doc(db, "projects", projectDoc.id);
    const currentRoles = projectData.roles || {};
    currentRoles[userId] = 'full';

    await updateDoc(docRef, {
      collaborators: arrayUnion(userId),
      roles: currentRoles,
      updatedAt: new Date().toISOString()
    });
    
    return projectDoc.id;
  } catch (error) {
    console.error("[Database] Error joining project:", error);
    return null;
  }
};

export const getUserSubscription = async (userId: string): Promise<UserSubscription | null> => {
  try {
    const subsRef = collection(db, "customers", userId, "subscriptions");
    const q = query(subsRef, where("status", "in", ["active", "trialing"]));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    return snap.docs[0].data() as UserSubscription;
  } catch (error) {
    console.error("[Database] Subscription error:", error);
    return null;
  }
};
